import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Popover,
  Button
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddIcon from '@mui/icons-material/Add';
import WidgetsIcon from '@mui/icons-material/Widgets';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useUser } from '../WebSocketContext';
import { formatTimestamp } from '../../Utils/dateUtils';

const WidgetAddIcon = () => (
  <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
    <WidgetsIcon sx={{ fontSize: '1rem' }} />
    <AddIcon
      sx={{
        position: 'absolute',
        right: -6,
        bottom: -6,
        fontSize: '0.7rem',
        color: 'primary.contrastText',
        backgroundColor: 'primary.main',
        borderRadius: '50%',
        p: '1px'
      }}
    />
  </Box>
);

const WidgetDashboardHeader = ({
  snapshots = [],
  isLoading = false,
  selectedSnapshotId = '',
  onSnapshotSelect,
  isSaving = false,
  isSnapshotMode = false,
  onSaveSnapshot,
  onClearSnapshot,
  error = '',
  activeSnapshot = null,
  showHiddenWidgets = false,
  onToggleHiddenWidgets,
  showAddWidget = false,
  onAddWidget
}) => {
  const { t } = useTranslation();
  const user = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Info popover state (used for chip hover/click)
  const [infoPopover, setInfoPopover] = useState({ anchorEl: null, snapshot: null });
  const openInfoPopover = useCallback((event, snapshot) => {
    // prevent menu item click from closing parent menus
    if (event && event.stopPropagation) event.stopPropagation();
    setInfoPopover({ anchorEl: event.currentTarget, snapshot });
  }, []);
  const closeInfoPopover = useCallback(() => setInfoPopover({ anchorEl: null, snapshot: null }), []);

  const infoOpen = Boolean(infoPopover.anchorEl) && !!infoPopover.snapshot;

  const selectedSnapshot = useMemo(() => {
    if (!selectedSnapshotId) {
      return null;
    }
    return snapshots.find((snapshot) => String(snapshot.id) === String(selectedSnapshotId)) || null;
  }, [snapshots, selectedSnapshotId]);



  // Compact single-line representation for the selected value (prevents Select from growing taller)
  const snapshotCompact = useMemo(() => {
    if (!selectedSnapshot) return null;
    const createdBy = selectedSnapshot.created_by_name || t('dashboard.snapshot_unknown_user', 'Unknown');
    const timestamp = formatTimestamp(selectedSnapshot.created_at);
    const count = selectedSnapshot.widget_history_count ?? 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
        <Typography noWrap variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {`${timestamp} — ${createdBy}`}
        </Typography>
        <Box sx={{ flexShrink: 0 }}>
          <Chip size="small" label={`${count}`} color="primary" sx={{ fontWeight: 600 }} />
        </Box>
      </Box>
    );
  }, [selectedSnapshot, t]);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 300, flex: isMobile ? 1 : undefined, maxWidth: isMobile ? '100%' : 900 }} disabled={isLoading}>
            <InputLabel>{t('dashboard.snapshot_select', 'Live dashboard')}</InputLabel>
            <Select
              sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
              value={selectedSnapshotId}
              label={t('dashboard.snapshot_select', 'Live dashboard')}
              onChange={(event) => onSnapshotSelect(String(event.target.value))}
              renderValue={(value) => {
                if (!value) return t('dashboard.snapshot_none', 'Select a snapshot');
                return snapshotCompact || t('dashboard.snapshot_selected', 'Snapshot selected');
              }}
              MenuProps={{
                PaperProps: { sx: { maxHeight: 48 * 6, width: isMobile ? 'auto' : 520 } },
                MenuListProps: { dense: true }
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('dashboard.snapshot_live', 'Live dashboard')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('dashboard.snapshot_live_hint', 'Choose this to return to the live dashboard')}
                  </Typography>
                </Box>
              </MenuItem>
              {snapshots.map((snapshot) => {
                const createdBy = snapshot.created_by_name || t('dashboard.snapshot_unknown_user', 'Unknown');
                const timestamp = formatTimestamp(snapshot.created_at);
                const count = snapshot.widget_history_count ?? 0;
                const comment = snapshot.comment || '';
                return (
                  <MenuItem
                    key={snapshot.id}
                    value={snapshot.id}
                    sx={{ alignItems: 'flex-start', py: 1.25, gap: 1, minHeight: 64 }}
                  >
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap variant="body2" sx={{ fontWeight: 600 }}>
                          {timestamp}
                        </Typography>
                        <Typography noWrap variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {createdBy}{comment ? ` — ${comment}` : ''}
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0, pl: 1, display: 'flex', alignItems: 'center' }}>
                        <Tooltip
                          title={`${count} ${t('dashboard.snapshot_widgets', 'widgets')} — ${formatTimestamp(snapshot.created_at)}`}
                          enterDelay={250}
                          disableInteractive={false}
                        >
                          <span>
                            <Chip
                              size="small"
                              icon={<WidgetsIcon fontSize="small" />}
                              label={`${count}`}
                              color="primary"
                              sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                              aria-label={t('dashboard.snapshot_widgets_count', { count })}
                              onClick={(e) => openInfoPopover(e, snapshot)}
                              onMouseDown={(e) => e.stopPropagation()}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {user.canManageProject && (
            <Tooltip title={t('dashboard.snapshot_save', 'Save snapshot')}>
              <span>
                <IconButton
                  color="primary"
                  onClick={onSaveSnapshot}
                  disabled={isSaving || isSnapshotMode}
                  aria-label={t('dashboard.snapshot_save', 'Save snapshot')}
                >
                  <SaveOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onToggleHiddenWidgets && (
            <Tooltip
              title={
                showHiddenWidgets
                  ? t('widget.hide_hidden_widgets', 'Hide hidden widgets')
                  : t('widget.show_hidden_widgets', 'Show hidden widgets')
              }
            >
              <span>
                <IconButton
                  color="primary"
                  onClick={onToggleHiddenWidgets}
                  disabled={isSnapshotMode}
                  aria-label={
                    showHiddenWidgets
                      ? t('widget.hide_hidden_widgets', 'Hide hidden widgets')
                      : t('widget.show_hidden_widgets', 'Show hidden widgets')
                  }
                >
                  {showHiddenWidgets ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showAddWidget && (
            isMobile ? (
              <Tooltip title={t('widget.add_widget', 'Add widget')}>
                <span>
                  <IconButton
                    color="primary"
                    onClick={onAddWidget}
                    disabled={isSnapshotMode}
                    aria-label={t('widget.add_widget', 'Add widget')}
                    sx={{
                      width: 34,
                      height: 34,
                      border: (theme) => `1px solid ${theme.palette.primary.dark}`,
                      borderRadius: 1.25,
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark'
                      }
                    }}
                  >
                    <WidgetAddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<WidgetAddIcon />}
              onClick={onAddWidget}
              disabled={isSnapshotMode}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
              aria-label={t('widget.add_widget', 'Add widget')}
            >
                {t('widget.add_widget', 'Add widget')}
              </Button>
            )
          )}
        </Box>

      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {isSnapshotMode && activeSnapshot && (
        <Alert
          severity="info"
          sx={(theme) => ({
            mt: 1,
            borderRadius: 2,
            py: 0.75,
            px: 1.25,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(87,87,176,0.06)' : 'rgba(87,87,176,0.03)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(87,87,176,0.08)',
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            boxShadow: 'none'
          })}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0, width: '100%' }}>
            {/* Primary row: title/description + widget count (single line, no wrap) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0
                }}
                title={activeSnapshot.comment || `${t('dashboard.snapshot_viewing', 'Viewing snapshot')} — ${formatTimestamp(activeSnapshot.created_at)}`}
              >
                {activeSnapshot.comment || `${t('dashboard.snapshot_viewing', 'Viewing snapshot')} — ${formatTimestamp(activeSnapshot.created_at)}`}
              </Typography>

              <Box sx={{ flexShrink: 0, ml: 1, display: 'flex', alignItems: 'center' }}>
                <Tooltip
                  title={`${activeSnapshot.widget_history_count ?? 0} ${t('dashboard.snapshot_widgets', 'widgets')}`}
                  enterDelay={250}
                >
                  <span>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<WidgetsIcon fontSize="small" />}
                      label={`${activeSnapshot.widget_history_count ?? 0}`}
                      aria-label={t('dashboard.snapshot_widgets_count', { count: activeSnapshot.widget_history_count ?? 0 })}
                      sx={{ borderRadius: 1, fontWeight: 600, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                      onClick={(e) => openInfoPopover(e, activeSnapshot)}
                      aria-haspopup="true"
                      aria-expanded={infoOpen}
                    />
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Secondary row: username + date (muted, smaller) */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.675rem', sm: '0.75rem' } }}
              title={`${activeSnapshot.created_by_name || t('dashboard.snapshot_unknown_user', 'Unknown')} — ${formatTimestamp(activeSnapshot.created_at)}`}
            >
              {activeSnapshot.created_by_name || t('dashboard.snapshot_unknown_user', 'Unknown')} — {formatTimestamp(activeSnapshot.created_at)}
            </Typography>
          </Box>
        </Alert>
      )}

      <Popover
        open={infoOpen}
        anchorEl={infoPopover.anchorEl}
        onClose={closeInfoPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 2, maxWidth: 360, borderRadius: 2 } }}
      >
        {infoPopover.snapshot && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {infoPopover.snapshot.comment || t('dashboard.snapshot_viewing', 'Snapshot')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {t('dashboard.snapshot_saved_by', 'Saved by')} {infoPopover.snapshot.created_by_name || t('dashboard.snapshot_unknown_user', 'Unknown')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              {formatTimestamp(infoPopover.snapshot.created_at)}
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WidgetsIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {infoPopover.snapshot.widget_history_count ?? 0} {t('dashboard.snapshot_widgets', 'widgets')}
                </Typography>
              </Box>
              <Button size="small" onClick={closeInfoPopover} sx={{ textTransform: 'none' }}>
                {t('common.close', 'Close')}
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default WidgetDashboardHeader;
